// Slice-10 authenticated shell — nav bar + main content outlet.

import { Link, Outlet, useRouter } from "@tanstack/react-router";
import { useAuth } from "../auth/AuthContext";
import { useTheme } from "../theme/ThemeContext";
import { useI18n } from "../i18n/I18nContext";

export function AppShell(): JSX.Element {
  const { user, logout } = useAuth();
  const { mode, toggle } = useTheme();
  const { t, locale, setLocale } = useI18n();
  const router = useRouter();

  const handleLogout = async () => {
    await logout();
    await router.navigate({ to: "/login" });
  };

  return (
    <div data-testid="app-shell">
      <nav className="rc-nav" aria-label="Primary">
        <strong>{t("app.title")}</strong>
        <Link to="/me" className="rc-nav__link">{t("nav.profile")}</Link>
        {/* Slice F (v2) / ADR 0006v2: gate by UI hints only — these are NOT
            authorization checks. Permission names are the new lowercase dotted
            forms (users.read, roles.read, permissions.read). The server
            enforces authoritatively on every navigation that triggers an API
            call. */}
        {user?.uiHints.includes("users.read") && (
          <Link to="/users" className="rc-nav__link">{t("nav.users")}</Link>
        )}
        {user?.uiHints.includes("roles.read") && (
          <Link to="/roles" className="rc-nav__link">{t("nav.roles")}</Link>
        )}
        {user?.uiHints.includes("permissions.read") && (
          <Link to="/permissions" className="rc-nav__link">{t("nav.permissions")}</Link>
        )}
        {/* Module 3 / Slice 8 — organization admin nav (UI hint gate only;
            server enforces organization.read on each request). */}
        {user?.uiHints.includes("organization.read") && (
          <>
            <Link to="/admin/organization/units" className="rc-nav__link">
              {t("nav.organizationUnits")}
            </Link>
            <Link to="/admin/organization/unit-types" className="rc-nav__link">
              {t("nav.organizationUnitTypes")}
            </Link>
          </>
        )}
        {/* Module 4 / Slice 5 — workforce admin nav (UI hint gate only;
            server enforces workforce.employee.read on each request). */}
        {user?.uiHints.includes("workforce.employee.read") && (
          <Link to="/admin/workforce/employees" className="rc-nav__link">
            {t("nav.workforceEmployees")}
          </Link>
        )}
        {/* Module 6 / Slice 5 — crewing admin nav (UI hint gate only;
            server enforces crewing.read on each request). PATTERNS.md §2.1
            + MODULE_6_PLAN.md §10. */}
        {user?.uiHints.includes("crewing.read") && (
          <Link to="/admin/crewing/vehicles" className="rc-nav__link">
            {t("nav.crewingVehicles")}
          </Link>
        )}
        <span className="rc-nav__spacer" />
        <button
          type="button"
          className="rc-button"
          onClick={() => setLocale(locale === "en-US" ? "es" : "en-US")}
          aria-label={t("nav.locale.toggle")}
        >
          {locale === "en-US" ? "ES" : "EN"}
        </button>
        <button
          type="button"
          className="rc-button"
          onClick={toggle}
          aria-label={t("nav.theme.toggle")}
        >
          {mode === "light" ? "🌙" : "☀️"}
        </button>
        <button
          type="button"
          className="rc-button"
          data-testid="sign-out"
          onClick={() => void handleLogout()}
        >
          {t("nav.logout")}
        </button>
      </nav>
      <main className="rc-page">
        <Outlet />
      </main>
    </div>
  );
}
